import { Transcript, TranscriptFolder } from "../../types/transcripts";

export type TranscriptLocation =
  { type: "all" } | { type: "unfiled" } | { type: "folder"; folderId: number };

export interface TranscriptNavigationState {
  location: TranscriptLocation;
  locationGeneration: number;
  selectedTranscriptIds: Set<number>;
  selectedFolderIds: Set<number>;
}

export type TranscriptNavigationAction =
  | { type: "location_changed"; location: TranscriptLocation }
  | {
      type: "transcript_selection_changed";
      transcriptId: number;
      selected: boolean;
    }
  | { type: "folder_selection_changed"; folderId: number; selected: boolean }
  | { type: "folder_removed"; folderId: number }
  | { type: "clear_selection" };

export const transcriptNavigationReducer = (
  state: TranscriptNavigationState,
  action: TranscriptNavigationAction,
): TranscriptNavigationState => {
  if (action.type === "location_changed") {
    return {
      location: action.location,
      locationGeneration: state.locationGeneration + 1,
      selectedTranscriptIds: new Set(),
      selectedFolderIds: new Set(),
    };
  }
  if (action.type === "clear_selection") {
    return {
      ...state,
      selectedTranscriptIds: new Set(),
      selectedFolderIds: new Set(),
    };
  }

  if (action.type === "folder_removed") {
    const selectedFolderIds = new Set(state.selectedFolderIds);
    selectedFolderIds.delete(action.folderId);
    return { ...state, selectedFolderIds };
  }

  if (action.type === "folder_selection_changed") {
    const selectedFolderIds = new Set(state.selectedFolderIds);
    if (action.selected) selectedFolderIds.add(action.folderId);
    else selectedFolderIds.delete(action.folderId);
    return { ...state, selectedFolderIds };
  }

  const selectedTranscriptIds = new Set(state.selectedTranscriptIds);
  if (action.selected) selectedTranscriptIds.add(action.transcriptId);
  else selectedTranscriptIds.delete(action.transcriptId);
  return { ...state, selectedTranscriptIds };
};

export const getBulkSelectedTranscriptIds = (
  state: TranscriptNavigationState,
): number[] => Array.from(state.selectedTranscriptIds);

export const getBulkSelectedFolderIds = (
  state: TranscriptNavigationState,
): number[] => Array.from(state.selectedFolderIds);

export const getBulkSelectionCount = (
  state: TranscriptNavigationState,
): number => state.selectedTranscriptIds.size + state.selectedFolderIds.size;

export const isTranscriptLocationGenerationCurrent = (
  startedGeneration: number,
  currentGeneration: number,
): boolean => startedGeneration === currentGeneration;

export const filterTranscriptsForLocation = (
  transcripts: Transcript[],
  location: TranscriptLocation,
): Transcript[] => {
  if (location.type === "all") return transcripts;
  if (location.type === "unfiled") {
    return transcripts.filter((transcript) => transcript.folder_id == null);
  }
  return transcripts.filter(
    (transcript) => transcript.folder_id === location.folderId,
  );
};

export type TranscriptSortOrder = "recent" | "alphabetical";

export const sortTranscripts = (
  transcripts: Transcript[],
  order: TranscriptSortOrder,
): Transcript[] =>
  [...transcripts].sort((left, right) => {
    if (order === "alphabetical") return left.title.localeCompare(right.title);
    const leftDate = new Date(left.created_date).getTime();
    const rightDate = new Date(right.created_date).getTime();
    return (
      (Number.isFinite(rightDate) ? rightDate : 0) -
      (Number.isFinite(leftDate) ? leftDate : 0)
    );
  });

export const getTranscriptFolderPath = (
  folderId: number,
  folders: TranscriptFolder[],
): TranscriptFolder[] => {
  const folderById = new Map(
    folders.map((folder) => [folder.folder_id, folder]),
  );
  const path: TranscriptFolder[] = [];
  const seen = new Set<number>();
  let folder = folderById.get(folderId);

  while (folder && !seen.has(folder.folder_id)) {
    seen.add(folder.folder_id);
    path.unshift(folder);
    folder =
      folder.parent_folder_id == null
        ? undefined
        : folderById.get(folder.parent_folder_id);
  }

  return path;
};
